import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { BrandedLayout } from '@/components/BrandedLayout';

/**
 * Forgot-password page
 *
 * User enters their email; Supabase sends a reset link to /auth/reset.
 *
 * Per AUTH-AC-08 (Requirements §1.4):
 *   "If a user requests a password reset for an email, the response is
 *    always 'If an account exists for that email, a reset link has been
 *    sent.' (enumeration-safe)"
 *
 * That is what Supabase's resetPasswordForEmail() does — it returns success
 * regardless of whether the email exists. We display that exact message.
 */
export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);

    if (!email.trim()) {
      setError('Email is required.');
      return;
    }

    setSubmitting(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(
        email.trim(),
        {
          redirectTo: `${window.location.origin}/auth/reset`,
        }
      );
      // Even on error we show the same enumeration-safe message; only
      // network-level failures bubble.
      if (error && error.message.toLowerCase().includes('rate')) {
        setError(error.message);
        return;
      }
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <BrandedLayout>
      <div className="container mx-auto py-12 px-4">
        <div className="mx-auto w-full max-w-sm space-y-6">
          <div className="text-center space-y-1">
            <h1 className="text-2xl font-semibold tracking-tight">
              Reset your password
            </h1>
            <p className="text-sm text-muted-foreground">
              Enter the email associated with your account.
            </p>
          </div>

          {submitted ? (
            <div className="space-y-4">
              <p className="rounded-md border border-border bg-muted/40 p-4 text-sm">
                If an account exists for that email, a reset link has been sent.
                The link expires in 1 hour.
              </p>
              <Link
                to="/auth/sign-in"
                className="block text-center text-sm underline"
              >
                Back to sign in
              </Link>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-4">
              <div className="space-y-1">
                <label htmlFor="email" className="block text-sm font-medium">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={inputClass}
                />
              </div>

              {error && (
                <p
                  role="alert"
                  className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive"
                >
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
              >
                {submitting ? 'Sending...' : 'Send reset link'}
              </button>

              <p className="text-center text-sm">
                <Link to="/auth/sign-in" className="text-muted-foreground hover:underline">
                  Back to sign in
                </Link>
              </p>
            </form>
          )}
        </div>
      </div>
    </BrandedLayout>
  );
}

const inputClass =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2';
